var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Modal, ModalFooter, } from "@folio/stripes/components";
import { useCallout, useOkapiKy } from "@folio/stripes/core";
import { useMutation } from "react-query";
import { useModal } from "../utils/useModal";
import { getErrorInfo } from "../utils/getErrorCode";
import { getUserErrorMessageId } from "../utils/getErrorMessageId";
import ActionErrorModal from "./ActionErrorModal";
/**
 * Identifies this modal's reset mutation — the form that renders the modal has its own save
 * mutation, and the tests tell the two apart by it.
 */
export var WELCOME_EMAIL_RESET_MUTATION_KEY = [
    "czna-users",
    "welcome-email-password-reset",
];
/**
 * CZNA-1589 — the user was created, but Keycloak could not send the welcome e-mail with the
 * password setup link (US-04 A4: that does not undo the creation). A user created without a
 * password has no other way in, so the administrator is told and offered a password reset,
 * which sends the same link through the existing `POST /users/{id}/password-reset`.
 *
 * <p>A failed reset shows the error modal and then brings the warning back: the link still
 * has not gone out, so the administrator can try again or close it knowingly. The success
 * callout is the app-wide one (`useCallout`), because `onDone` usually leaves the form and a
 * callout rendered here would vanish with it.
 */
var WelcomeEmailFailedModal = function (_a) {
    var userId = _a.userId, email = _a.email, onDone = _a.onDone;
    var intl = useIntl();
    var ky = useOkapiKy();
    var callout = useCallout();
    var _b = useModal(), errorOpen = _b.isOpen, errorMessage = _b.message, openErrorModal = _b.openModal, closeErrorModal = _b.closeModal;
    var resetPasswordMutation = useMutation(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, ky.post("czna-users/users/".concat(userId, "/password-reset"))];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, {
        mutationKey: WELCOME_EMAIL_RESET_MUTATION_KEY,
        onSuccess: function () {
            callout === null || callout === void 0 ? void 0 : callout.sendCallout({
                type: "success",
                message: intl.formatMessage({
                    id: "ui-czna-users.users.welcomeEmailFailed.resetPassword.success",
                }, { email: email }),
            });
            onDone();
        },
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var errorInfo;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        errorInfo = _a.sent();
                        openErrorModal(intl.formatMessage({ id: getUserErrorMessageId(errorInfo) }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: function () { return resetPasswordMutation.mutate(); }, disabled: resetPasswordMutation.isLoading },
            React.createElement(FormattedMessage, { id: "ui-czna-users.users.welcomeEmailFailed.resetPassword" })),
        React.createElement(Button, { onClick: onDone, disabled: resetPasswordMutation.isLoading },
            React.createElement(FormattedMessage, { id: "ui-czna-users.users.welcomeEmailFailed.close" }))));
    return (React.createElement(React.Fragment, null,
        React.createElement(Modal, { open: !errorOpen, label: intl.formatMessage({
                id: "ui-czna-users.users.welcomeEmailFailed.title",
            }), onClose: onDone, dismissible: true, footer: footer, size: "small" },
            React.createElement("p", null, intl.formatMessage({ id: "ui-czna-users.users.welcomeEmailFailed.message" }, { email: email }))),
        React.createElement(ActionErrorModal, { open: errorOpen, message: errorMessage, onClose: closeErrorModal })));
};
export default WelcomeEmailFailedModal;
